import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCurrentUser, usePermissions } from '@hetivo/hetivo-lib-front-utils';
import { AuthenticatedLayout, ImpersonationBanner, RouteErrorFallback } from '@hetivo/hetivo-lib-web-utils';
import { useAuth } from '&/auth';
import { buildSidebarNavItems, env } from '&/config';
import { applyIssuesBadge } from '&/features/issues/lib/nav-badge';
import { useIssuesOpenCount } from '&/features/issues/hooks/use-issues-open-count';
import { useServiceReminderOverdueCount } from '&/features/service-reminders';
import { applyRemindersBadge } from '&/features/service-reminders/lib/nav-badge';
import { useMasterAutoInit } from '&/hooks/use-master-auto-init';
import { useTenantModules } from '&/hooks/use-tenant-modules';
import { useUserScopes } from '&/hooks/use-user-scopes';
import { SUPPORTED_LANGUAGES } from '&/i18n/config';
import { MasterContextProvider, useMasterContext } from '&/master';

function AuthenticatedErrorFallback({ error, reset }: ErrorComponentProps): React.JSX.Element {
  return <RouteErrorFallback error={error} isDev={env.isDev} onReset={reset} />;
}

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: ({ context, location }): void => {
    // Don't redirect while auth state is still being determined (e.g., Firebase
    // hasn't fired onAuthStateChanged yet). The component will show a loading
    // page until isLoading becomes false.
    if (context.auth.isLoading) return;
    if (!context.auth.isAuthenticated) {
      throw redirect({
        search: { redirect: location.pathname },
        to: '/login',
      });
    }
  },
  component: FrontofficeAuthenticatedLayout,
  errorComponent: AuthenticatedErrorFallback,
});

function FrontofficeAuthenticatedLayout(): React.JSX.Element {
  return (
    <MasterContextProvider>
      <FrontofficeAuthenticatedLayoutInner />
    </MasterContextProvider>
  );
}

function FrontofficeAuthenticatedLayoutInner(): React.JSX.Element {
  const auth = useAuth();
  const { t } = useTranslation();
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions();
  const { user: backendUser } = useCurrentUser(auth.isAuthenticated);
  const scopeInfo = useUserScopes(auth.isAuthenticated ? (backendUser ?? null) : null);
  const masterContext = useMasterContext();
  const { hasModule } = useTenantModules();
  // The Overdue count of the Reminders badge: disabled without the Maintenance module or service-plans:list.
  const overdueReminderCount = useServiceReminderOverdueCount();

  useMasterAutoInit({ scopeInfo, user: backendUser });

  const openIssueCount = useIssuesOpenCount();
  const sidebarNavItems = applyRemindersBadge(
    applyIssuesBadge(
      buildSidebarNavItems({
        ...scopeInfo,
        hasModule,
        isScopeResolved: backendUser !== null && !isPermissionsLoading,
        siteId: masterContext.siteId,
        territoryId: masterContext.territoryId,
      }),
      openIssueCount,
      openIssueCount === undefined ? '' : t('pages.issues.openCountAria', { count: openIssueCount }),
    ),
    overdueReminderCount,
    overdueReminderCount === undefined ? '' : t('navigation.remindersOverdueBadge', { count: overdueReminderCount }),
  );

  const handleDisconnect = async (): Promise<void> => {
    await auth.signOut();
    // After sign-out, the auth state will become unauthenticated
    // and the beforeLoad guard will redirect to /login
  };

  const topBanner = auth.isImpersonating ? (
    <ImpersonationBanner
      disconnectLabel={t('impersonation.disconnect')}
      message={
        <Trans
          components={{ strong: <strong /> }}
          i18nKey="impersonation.viewingAs"
          values={{ email: auth.user?.email }}
        />
      }
      onDisconnect={(): void => void handleDisconnect()}
    />
  ) : undefined;

  return (
    <AuthenticatedLayout
      auth={auth}
      backendUser={backendUser}
      hasPermission={hasPermission}
      isPermissionsLoading={isPermissionsLoading}
      languages={[...SUPPORTED_LANGUAGES]}
      logo={
        <Link className="flex flex-col items-start" to="/">
          <img
            alt={t('app.logoAlt')}
            className="h-7 max-w-32 object-contain group-data-[collapsible=icon]:hidden"
            src={`${import.meta.env.BASE_URL}hetivo-logo-orange.png`}
          />
          <div className="hidden h-8 w-8 items-center justify-center rounded-full bg-sidebar-primary group-data-[collapsible=icon]:flex">
            <span className="text-sm font-bold text-white">H</span>
          </div>
          <p className="mt-0.5 text-xs leading-tight text-sidebar-foreground/60 group-data-[collapsible=icon]:hidden">
            {t('app.subtitle')}
          </p>
        </Link>
      }
      navItems={sidebarNavItems}
      requiredPermission="frontoffice:access"
      topBanner={topBanner}
    />
  );
}
